import { useState } from 'react';
import { Eye, ListPlus } from 'lucide-react';

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

import { formatDetailValue } from './AdditionalDetailsCell';
import { TYPE_LABELS, type CustomField, type CustomFieldTemplate } from './customFields';

function limitsText(f: CustomField): string {
  if (f.field_type === 'Multi Select') return (f.options || []).join(', ') || '—';
  const min = f.field_min ?? null;
  const max = f.field_max ?? null;
  if (min === null && max === null) return '—';
  if (min !== null && max !== null) return `${min} – ${max}`;
  return min !== null ? `Min ${min}` : `Max ${max}`;
}

/** Template list: "View (N)" button; the template's fields open in a modal. */
export function TemplateFieldsCell({ template }: { template: CustomFieldTemplate }) {
  const [open, setOpen] = useState(false);
  const fields = template.fields || [];
  if (!fields.length) return <span className="text-muted-foreground">—</span>;

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-md bg-primary/10 px-2 py-1 text-[11px] font-semibold text-primary ring-1 ring-inset ring-primary/20 transition hover:bg-primary/15"
      >
        <Eye className="h-3.5 w-3.5" />
        View ({fields.length})
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ListPlus className="h-4 w-4 text-primary" />
              {template.name}
            </DialogTitle>
            <DialogDescription>
              {fields.length} field{fields.length === 1 ? '' : 's'} in this template
            </DialogDescription>
          </DialogHeader>
          <div className="max-h-[60vh] overflow-auto rounded-lg border border-border/60">
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-muted text-left text-xs uppercase tracking-wide text-muted-foreground">
                <tr>
                  <th className="px-3 py-2 font-semibold">Field</th>
                  <th className="px-3 py-2 font-semibold">Type</th>
                  <th className="px-3 py-2 font-semibold">Required</th>
                  <th className="px-3 py-2 font-semibold">Default</th>
                  <th className="px-3 py-2 font-semibold">Min / Max / Options</th>
                </tr>
              </thead>
              <tbody>
                {fields.map((f) => (
                  <tr key={f.field_name} className="border-t border-border/50 align-top">
                    <td className="px-3 py-2 font-medium text-foreground">{f.field_name}</td>
                    <td className="whitespace-nowrap px-3 py-2 text-muted-foreground">{TYPE_LABELS[f.field_type]}</td>
                    <td className="px-3 py-2">
                      {f.is_required ? (
                        <span className="font-semibold text-destructive">Yes</span>
                      ) : (
                        <span className="text-muted-foreground">No</span>
                      )}
                    </td>
                    <td className="break-words px-3 py-2 text-muted-foreground">{formatDetailValue(f.field_default)}</td>
                    <td className="break-words px-3 py-2 text-muted-foreground">{limitsText(f)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
